import { ChangeEvent, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Loading from '../shared/Loading';
import { getAuthHeader } from '../../helpers/authHelper';
import { showErrorAlert } from '../../helpers/alertHelper';
import VolunteerModel from '../../models/VolunteerModel';
import Swal from 'sweetalert2';

interface GetVolunteersResponse {
    volunteers: VolunteerModel[];
}

const fetchVolunteers = async (): Promise<GetVolunteersResponse> => {
    const resp = await fetch("/api/admin/get-volunteers", {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': getAuthHeader(),
        }
    });

    if (!resp.ok) {
        throw new Error("Failed to fetch volunteers");
    }

    return resp.json();
};

const Volunteers = () => {
    const queryClient = useQueryClient();

    //
    // State
    //
    const [searchTerm, setSearchTerm] = useState('');
    const [newVolunteer, setNewVolunteer] = useState({ firstName: '', lastName: '', shirtSize: 'Small' });
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [editedVolunteer, setEditedVolunteer] = useState({ volunteerId: 0, firstName: '', lastName: '', shirtSize: 'Small' });

    //
    // Query
    //
    const { data, isLoading } = useQuery<GetVolunteersResponse>({
        queryKey: ['volunteers'],
        queryFn: fetchVolunteers,
    });

    const volunteers = data?.volunteers ?? [];
    const visibleVolunteers = searchTerm
        ? volunteers.filter(v => v.fullName?.toLowerCase().includes(searchTerm))
        : volunteers;

    //
    // Mutations
    //
    const addMutation = useMutation({
        mutationFn: async (volunteer: { firstName: string; lastName: string; shirtSize: string }) => {
            const resp = await fetch("/api/admin/add-volunteer", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': getAuthHeader(),
                },
                body: JSON.stringify(volunteer)
            });
            if (!resp.ok) throw new Error("Failed to add volunteer");
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['volunteers'] });
            setNewVolunteer({ firstName: '', lastName: '', shirtSize: 'Small' });
        },
        onError: () => showErrorAlert(),
    });

    const editMutation = useMutation({
        mutationFn: async (volunteer: typeof editedVolunteer) => {
            const resp = await fetch("/api/admin/edit-volunteer", {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': getAuthHeader(),
                },
                body: JSON.stringify(volunteer)
            });
            if (!resp.ok) throw new Error("Failed to edit volunteer");
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['volunteers'] });
            setEditIndex(null);
        },
        onError: () => showErrorAlert(),
    });

    const deleteMutation = useMutation({
        mutationFn: async (volunteerId: number) => {
            const resp = await fetch(`/api/admin/delete-volunteer/${volunteerId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': getAuthHeader(),
                }
            });
            if (!resp.ok) throw new Error("Failed to delete volunteer");
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['volunteers'] });
        },
        onError: () => showErrorAlert(),
    });

    //
    // Handlers
    //
    function handleSearch(e: ChangeEvent<HTMLInputElement>): void {
        setSearchTerm(e.target.value.toLowerCase());
    }

    const handleAddVolunteer = () => {
        if (!newVolunteer.firstName || !newVolunteer.lastName || !newVolunteer.shirtSize) {
            return;
        }
        addMutation.mutate(newVolunteer);
    };

    const handleEditVolunteer = (volunteer: VolunteerModel) => {
        setEditIndex(visibleVolunteers.indexOf(volunteer));
        setEditedVolunteer({
            volunteerId: volunteer.volunteerId,
            firstName: volunteer.firstName || '',
            lastName: volunteer.lastName || '',
            shirtSize: volunteer.shirtSize || 'Small'
        });
    };

    const handleSaveEdit = () => {
        if (editIndex === null) return;
        editMutation.mutate(editedVolunteer);
    };

    const handleDeleteVolunteer = (index: number) => {
        const volunteerId = visibleVolunteers[index].volunteerId;

        Swal.fire({
            title: 'Are you sure?',
            text: "This will permanently delete the Volunteer.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Delete'
        }).then((result) => {
            if (result.isConfirmed) {
                deleteMutation.mutate(volunteerId);
            }
        });
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>, isEdit: boolean = false) => {
        const { name, value } = e.target;
        if (isEdit) {
            setEditedVolunteer({ ...editedVolunteer, [name]: value });
        } else {
            setNewVolunteer({ ...newVolunteer, [name]: value });
        }
    };

    return (
        <div className="container p-3">
            <h2 className="text-center">Volunteers</h2>

            {isLoading && <Loading />}

            <div className="my-3 row justify-content-center">
                <div className="col-12 col-md-8">
                    <div className="input-group">
                        <div className="input-group-prepend">
                            <div className="input-group-text search-input-icon py-2">
                                <i className="bi bi-search"></i>
                            </div>
                        </div>
                        <input
                            className="form-control py-2 border-right-0 border"
                            type="search"
                            placeholder="Search"
                            onChange={(e) => { handleSearch(e) }}
                        />
                    </div>
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-striped table-hover table-bordered">
                    <thead>
                        <tr>
                            <th>First Name</th>
                            <th>Last Name</th>
                            <th>Shirt Size</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleVolunteers && visibleVolunteers.map((volunteer, index) => (
                            <tr key={index}>
                                {editIndex === index ? (
                                    <>
                                        <td><input className="form-control" type="text" name="firstName" value={editedVolunteer.firstName} onChange={(e) => handleInputChange(e, true)} /></td>
                                        <td><input className="form-control" type="text" name="lastName" value={editedVolunteer.lastName} onChange={(e) => handleInputChange(e, true)} /></td>
                                        <td>
                                            <select className="form-control" name="shirtSize" value={editedVolunteer.shirtSize} onChange={(e) => handleInputChange(e, true)}>
                                                <option value="Small">Small</option>
                                                <option value="Medium">Medium</option>
                                                <option value="Large">Large</option>
                                                <option value="XL">XL</option>
                                                <option value="XXL">XXL</option>
                                                <option value="XXXL">XXXL</option>
                                            </select>
                                        </td>
                                        <td>
                                            <button className="btn btn-primary m-1" onClick={() => handleSaveEdit()}>Save</button>
                                            <button className="btn btn-danger" onClick={() => setEditIndex(null)}>Cancel</button>
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td>{volunteer.firstName}</td>
                                        <td>{volunteer.lastName}</td>
                                        <td>{volunteer.shirtSize}</td>
                                        <td>
                                            <button className="btn btn-primary m-1" onClick={() => handleEditVolunteer(volunteer)}>Edit</button>
                                            <button className="btn btn-danger" onClick={() => handleDeleteVolunteer(index)}>Delete</button>
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                        <tr>
                            <td><input className="form-control" type="text" name="firstName" value={newVolunteer.firstName} onChange={handleInputChange} /></td>
                            <td><input className="form-control" type="text" name="lastName" value={newVolunteer.lastName} onChange={handleInputChange} /></td>
                            <td>
                                <select className="form-control" name="shirtSize" value={newVolunteer.shirtSize} onChange={handleInputChange}>
                                    <option value="Small">Small</option>
                                    <option value="Medium">Medium</option>
                                    <option value="Large">Large</option>
                                    <option value="XL">XL</option>
                                    <option value="XXL">XXL</option>
                                    <option value="XXXL">XXXL</option>
                                </select>
                            </td>
                            <td>
                                <button className="btn btn-success m-1"
                                    onClick={handleAddVolunteer}
                                    disabled={!newVolunteer.firstName || !newVolunteer.lastName || !newVolunteer.shirtSize}
                                >
                                    Add
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default Volunteers;
